import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { walletAdminAPI } from '../../services/api';

/**
 * Admin: FoodWinc Wallet analytics — who added money, how, how much, and when.
 * Read-only. Shows the total money held (liability), a ledger reconciliation, a
 * per-user "who recharged" rollup, and the full recharge ledger. Optional date range.
 */
const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const csv = (rows) => rows.map((r) => r.map((c) => {
  const s = c == null ? '' : String(c);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}).join(',')).join('\n');
const download = (name, text) => {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href);
};

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px', minWidth: 170 };
const th = { textAlign: 'left', padding: '10px 12px', fontWeight: 600, borderBottom: '2px solid #e5e7eb', fontSize: 13, color: '#374151', whiteSpace: 'nowrap' };
const td = { padding: '9px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13 };
const btn = { padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const chip = (bg, fg) => ({ background: bg, color: fg, padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 });

function statusChip(s) {
  if (s === 'success') return <span style={chip('#dcfce7', '#166534')}>success</span>;
  if (s === 'pending') return <span style={chip('#fef9c3', '#854d0e')}>pending</span>;
  return <span style={chip('#fee2e2', '#991b1b')}>{s}</span>;
}

export default function WalletAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // --- Adjust a user's wallet ---
  const [adjPhone, setAdjPhone] = useState('');
  const [adjUsers, setAdjUsers] = useState(null);
  const [adjSel, setAdjSel] = useState(null);
  const [adjAmount, setAdjAmount] = useState('');
  const [adjDir, setAdjDir] = useState('credit');
  const [adjReason, setAdjReason] = useState('');
  const [adjBusy, setAdjBusy] = useState(false);
  const [adjMsg, setAdjMsg] = useState(null);

  const doLookup = async (preserveSelId = null) => {
    setAdjMsg(null);
    try {
      const r = await walletAdminAPI.lookupUser(adjPhone.trim());
      const users = r.data?.data?.users || [];
      setAdjUsers(users);
      setAdjSel(
        preserveSelId && users.some((u) => u.userId === preserveSelId) ? preserveSelId
          : users.length === 1 ? users[0].userId : null
      );
    } catch (e) {
      setAdjUsers([]); setAdjSel(null);
      setAdjMsg({ type: 'error', text: e.response?.data?.message || 'User not found' });
    }
  };

  const doAdjust = async () => {
    if (!adjSel) { setAdjMsg({ type: 'error', text: 'Select a user first' }); return; }
    const amt = Number(adjAmount);
    if (!(amt > 0)) { setAdjMsg({ type: 'error', text: 'Enter an amount greater than 0' }); return; }
    if (!adjReason.trim()) { setAdjMsg({ type: 'error', text: 'A reason is required' }); return; }
    const u = (adjUsers || []).find((x) => x.userId === adjSel);
    if (!window.confirm(`${adjDir === 'credit' ? 'ADD' : 'DEDUCT'} ₹${amt.toFixed(2)} ${adjDir === 'credit' ? 'to' : 'from'} ${u?.name || 'this user'}'s wallet?\n\nReason: ${adjReason.trim()}`)) return;
    setAdjBusy(true); setAdjMsg(null);
    try {
      const r = await walletAdminAPI.adjust({ userId: adjSel, amount: amt, direction: adjDir, reason: adjReason.trim() });
      setAdjMsg({ type: 'success', text: `${r.data?.message || 'Done.'} New balance: ₹${r.data?.data?.balance}` });
      setAdjAmount(''); setAdjReason('');
      await doLookup(adjSel);
    } catch (e) {
      setAdjMsg({ type: 'error', text: e.response?.data?.message || 'Failed to adjust wallet' });
    } finally { setAdjBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const r = await walletAdminAPI.getAnalytics(params);
      setData(r.data?.data || null);
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to load wallet analytics');
    } finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const s = data?.summary;
  const perUser = data?.perUser || [];
  const recharges = data?.recharges || [];
  const byType = data?.byType || {};
  const selectedUser = (adjUsers || []).find((u) => u.userId === adjSel) || null;

  const exportPerUser = () => download('wallet-users.csv', csv([
    ['Name', 'Phone', 'Email', 'Current Balance', 'Recharged', 'Recharge Count', 'Cashback', 'Spent', 'Refund', 'Last Recharge'],
    ...perUser.map((u) => [u.name, u.phone, u.email, u.currentBalance, u.recharged, u.rechargeCount, u.cashback, u.spent, u.refund, fmt(u.lastRechargeAt)]),
  ]));
  const exportRecharges = () => download('wallet-recharges.csv', csv([
    ['Name', 'Phone', 'Email', 'Amount', 'Status', 'Method', 'Cashfree Order ID', 'CF Payment ID', 'When'],
    ...recharges.map((t) => [t.name, t.phone, t.email, t.amount, t.status, t.method, t.cashfreeOrderId, t.cfPaymentId, fmt(t.createdAt)]),
  ]));

  return (
    <>
      <Navbar />
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '20px 16px 60px' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>💰 Wallet Analytics</h1>
        <p style={{ color: '#6b7280', marginBottom: 18 }}>Who added money to their FoodWinc wallet, how much, how, and when — plus the money held and a ledger reconciliation.</p>

        {/* Date range */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 18 }}>
          <label style={{ fontSize: 13 }}>From<br /><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ padding: 7, border: '1px solid #d1d5db', borderRadius: 8 }} /></label>
          <label style={{ fontSize: 13 }}>To<br /><input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ padding: 7, border: '1px solid #d1d5db', borderRadius: 8 }} /></label>
          <button style={{ ...btn, background: '#111827', color: '#fff', borderColor: '#111827' }} onClick={load}>Apply</button>
          <button style={btn} onClick={() => { setFrom(''); setTo(''); setTimeout(load, 0); }}>All time</button>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{from || to ? `Range: ${from || '…'} → ${to || '…'}` : 'Showing all-time'}</span>
        </div>

        {err && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, marginBottom: 16 }}>{err}</div>}

        {/* Adjust a user's wallet (credit/debit with a required reason) */}
        <div style={{ ...card, minWidth: 0, marginBottom: 22, borderColor: '#c7d2fe', background: '#eef2ff' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>🔧 Adjust a user's wallet</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>Credit or debit any user's FoodWinc wallet with a required reason. Every change is written to the wallet ledger (type "adjustment") with your admin name. A debit can't take the balance below ₹0.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
            <label style={{ fontSize: 13 }}>User phone<br />
              <input value={adjPhone} onChange={(e) => setAdjPhone(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doLookup(); }} placeholder="10-digit mobile" style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 8, width: 180 }} />
            </label>
            <button style={{ ...btn, background: '#111827', color: '#fff', borderColor: '#111827' }} onClick={() => doLookup()}>Look up</button>
          </div>

          {adjUsers && adjUsers.length > 1 && (
            <div style={{ marginBottom: 10, fontSize: 13 }}>
              <div style={{ color: '#6b7280', marginBottom: 4 }}>Multiple accounts on this number — pick one:</div>
              {adjUsers.map((u) => (
                <label key={u.userId} style={{ display: 'block', marginTop: 4 }}>
                  <input type="radio" name="adjsel" checked={adjSel === u.userId} onChange={() => setAdjSel(u.userId)} />{' '}
                  {u.name} · {u.phone} · [{(u.roles || []).join(', ') || 'user'}] · balance ₹{u.balance}
                </label>
              ))}
            </div>
          )}
          {adjUsers && adjUsers.length === 0 && <div style={{ fontSize: 13, color: '#991b1b' }}>No user found.</div>}

          {selectedUser && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
              <div style={{ fontWeight: 600 }}>{selectedUser.name} · {selectedUser.phone}</div>
              <div style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>Current balance: <b>₹{selectedUser.balance}</b>{(selectedUser.roles || []).length ? ` · [${selectedUser.roles.join(', ')}]` : ''}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <label style={{ fontSize: 13 }}>Action<br />
                  <select value={adjDir} onChange={(e) => setAdjDir(e.target.value)} style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 8 }}>
                    <option value="credit">Add (credit)</option>
                    <option value="debit">Deduct (debit)</option>
                  </select>
                </label>
                <label style={{ fontSize: 13 }}>Amount (₹)<br />
                  <input type="number" min="0" step="0.01" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} placeholder="0.00" style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 8, width: 120 }} />
                </label>
                <label style={{ fontSize: 13, flex: 1, minWidth: 220 }}>Reason (required)<br />
                  <input value={adjReason} onChange={(e) => setAdjReason(e.target.value)} placeholder="e.g. Goodwill credit for order #… / correcting duplicate recharge" style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 8, width: '100%' }} />
                </label>
                <button style={{ ...btn, background: adjDir === 'credit' ? '#166534' : '#991b1b', color: '#fff', borderColor: 'transparent', opacity: adjBusy ? 0.6 : 1 }} disabled={adjBusy} onClick={doAdjust}>
                  {adjBusy ? 'Applying…' : (adjDir === 'credit' ? 'Add to wallet' : 'Deduct from wallet')}
                </button>
              </div>
              {selectedUser.recent?.length > 0 && (
                <div style={{ marginTop: 12, overflowX: 'auto' }}>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Recent wallet movements</div>
                  <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                    <thead><tr><th style={th}>Type</th><th style={th}>Dir</th><th style={th}>Amount</th><th style={th}>Balance after</th><th style={th}>Note</th><th style={th}>When</th></tr></thead>
                    <tbody>
                      {selectedUser.recent.map((t, i) => (
                        <tr key={i}><td style={td}>{t.type}</td><td style={{ ...td, color: t.direction === 'credit' ? '#166534' : '#991b1b' }}>{t.direction}</td><td style={td}>₹{t.amount}</td><td style={td}>{t.balanceAfter != null ? `₹${t.balanceAfter}` : '—'}</td><td style={{ ...td, maxWidth: 280, whiteSpace: 'normal' }}>{t.note}</td><td style={td}>{fmt(t.at)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {adjMsg && (
            <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: adjMsg.type === 'success' ? '#f0fdf4' : '#fee2e2', color: adjMsg.type === 'success' ? '#166534' : '#991b1b', fontSize: 13 }}>{adjMsg.text}</div>
          )}
        </div>

        {loading && <div style={{ color: '#6b7280' }}>Loading…</div>}

        {s && !loading && (
          <>
            {/* KPI cards */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
              <div style={card}><div style={{ color: '#6b7280', fontSize: 13 }}>Money held in wallets</div><div style={{ fontSize: 26, fontWeight: 700 }}>{inr(s.balanceHeld)}</div><div style={{ fontSize: 12, color: '#9ca3af' }}>platform liability · {s.holders} holder(s)</div></div>
              <div style={card}><div style={{ color: '#6b7280', fontSize: 13 }}>Users who ever recharged</div><div style={{ fontSize: 26, fontWeight: 700 }}>{s.everRecharged}</div><div style={{ fontSize: 12, color: '#9ca3af' }}>all-time distinct</div></div>
              <div style={card}><div style={{ color: '#6b7280', fontSize: 13 }}>Recharges (success)</div><div style={{ fontSize: 26, fontWeight: 700 }}>{inr(s.rechargeSuccess.amount)}</div><div style={{ fontSize: 12, color: '#9ca3af' }}>{s.rechargeSuccess.count} payment(s){(from || to) ? ' · in range' : ''}</div></div>
              <div style={card}><div style={{ color: '#6b7280', fontSize: 13 }}>Pending / Failed</div><div style={{ fontSize: 20, fontWeight: 700 }}>{s.rechargePending.count} · {s.rechargeFailed.count}</div><div style={{ fontSize: 12, color: '#9ca3af' }}>{inr(s.rechargePending.amount)} pending</div></div>
              <div style={{ ...card, background: s.reconciles ? '#f0fdf4' : '#fef2f2', borderColor: s.reconciles ? '#bbf7d0' : '#fecaca' }}>
                <div style={{ color: '#6b7280', fontSize: 13 }}>Ledger reconciliation</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: s.reconciles ? '#166534' : '#991b1b' }}>{s.reconciles ? '✓ Balanced' : '⚠ Drift'}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>net ledger {inr(s.ledgerNet)} vs held {inr(s.balanceHeld)}</div>
              </div>
            </div>

            {/* Ledger by type */}
            <div style={{ ...card, minWidth: 0, marginBottom: 22 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Ledger by type (lifetime, successful movements)</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead><tr><th style={th}>Type</th><th style={th}>Credited (in)</th><th style={th}>Debited (out)</th><th style={th}>Txns</th></tr></thead>
                  <tbody>
                    {['recharge', 'cashback', 'refund', 'adjustment', 'order_payment'].map((t) => byType[t] ? (
                      <tr key={t}><td style={td}>{t}</td><td style={{ ...td, color: '#166534' }}>{byType[t].credit ? inr(byType[t].credit) : '—'}</td><td style={{ ...td, color: '#991b1b' }}>{byType[t].debit ? inr(byType[t].debit) : '—'}</td><td style={td}>{byType[t].count}</td></tr>
                    ) : null)}
                    {Object.keys(byType).length === 0 && <tr><td style={td} colSpan={4}>No movements yet.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>Money held = credits − debits. "Recharge" = added via Cashfree; "adjustment" = manual admin.</div>
            </div>

            {/* Per-user: who added money & how much */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700 }}>Who added money {(from || to) ? '(in range)' : ''} — {perUser.length} user(s)</h2>
              <button style={btn} onClick={exportPerUser} disabled={!perUser.length}>⬇ Export CSV</button>
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 28 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
                <thead><tr>
                  <th style={th}>User</th><th style={th}>Phone</th><th style={th}>Current balance</th><th style={th}>Recharged</th><th style={th}>#</th><th style={th}>Cashback</th><th style={th}>Spent</th><th style={th}>Refund</th><th style={th}>Last recharge</th>
                </tr></thead>
                <tbody>
                  {perUser.map((u) => (
                    <tr key={u.userId}>
                      <td style={td}>{u.name}{u.email ? <div style={{ fontSize: 11, color: '#9ca3af' }}>{u.email}</div> : null}</td>
                      <td style={td}>{u.phone}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{inr(u.currentBalance)}</td>
                      <td style={{ ...td, color: '#166534', fontWeight: 600 }}>{inr(u.recharged)}</td>
                      <td style={td}>{u.rechargeCount}</td>
                      <td style={td}>{u.cashback ? inr(u.cashback) : '—'}</td>
                      <td style={td}>{u.spent ? inr(u.spent) : '—'}</td>
                      <td style={td}>{u.refund ? inr(u.refund) : '—'}</td>
                      <td style={td}>{fmt(u.lastRechargeAt)}</td>
                    </tr>
                  ))}
                  {!perUser.length && <tr><td style={td} colSpan={9}>No wallet recharges {(from || to) ? 'in this range' : 'yet'}.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Recharge ledger: how & when */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700 }}>Recharge ledger — how & when ({recharges.length})</h2>
              <button style={btn} onClick={exportRecharges} disabled={!recharges.length}>⬇ Export CSV</button>
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 12 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 980 }}>
                <thead><tr>
                  <th style={th}>User</th><th style={th}>Phone</th><th style={th}>Amount</th><th style={th}>Status</th><th style={th}>Method</th><th style={th}>Reference</th><th style={th}>When</th>
                </tr></thead>
                <tbody>
                  {recharges.map((t) => (
                    <tr key={t.id}>
                      <td style={td}>{t.name}</td>
                      <td style={td}>{t.phone}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{inr(t.amount)}</td>
                      <td style={td}>{statusChip(t.status)}</td>
                      <td style={td}>{t.method}</td>
                      <td style={{ ...td, fontSize: 11, color: '#6b7280' }}>{t.cfPaymentId || t.cashfreeOrderId || '—'}</td>
                      <td style={td}>{fmt(t.createdAt)}</td>
                    </tr>
                  ))}
                  {!recharges.length && <tr><td style={td} colSpan={7}>No recharges {(from || to) ? 'in this range' : 'yet'}.</td></tr>}
                </tbody>
              </table>
            </div>
            {(data?.caps && (perUser.length >= data.caps.perUserLimit || recharges.length >= data.caps.rechargesLimit)) && (
              <div style={{ fontSize: 12, color: '#b45309', marginTop: 10 }}>Note: lists are capped at {data.caps.rechargesLimit} rows — narrow the date range to see older entries.</div>
            )}
          </>
        )}
      </div>
    </>
  );
}
